import { amenityService } from '@/services/amenityService'
import { Amenity } from '@/types/amenity'

interface CachedAmenities {
  data: Amenity[]
  timestamp: number
}

class AmenitiesManager {
  private static instance: AmenitiesManager
  private amenities: Amenity[] = []
  private amenitiesMap: Map<string, Amenity> = new Map()
  private amenitiesLoaded = false
  private loadingPromise: Promise<Amenity[]> | null = null
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 horas em millisegundos
  private readonly CACHE_KEY = 'meiuca-app-amenities'

  constructor() {
    this.loadFromLocalStorage()
  }

  static getInstance(): AmenitiesManager {
    if (!AmenitiesManager.instance) {
      AmenitiesManager.instance = new AmenitiesManager()
    }
    return AmenitiesManager.instance
  }

  private loadFromLocalStorage(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (cached) {
        const cachedData: CachedAmenities = JSON.parse(cached)
        if (this.isCacheValidFromTimestamp(cachedData.timestamp)) {
          this.amenities = cachedData.data
          this.buildAmenitiesMap()
          this.amenitiesLoaded = true
        } else {
          // Cache expirado, remove do localStorage
          localStorage.removeItem(this.CACHE_KEY)
        }
      }
    } catch (error) {
      console.warn('[MEIUCA-APP] Erro ao carregar cache do localStorage:', error)
      localStorage.removeItem(this.CACHE_KEY)
    }
  }

  private saveToLocalStorage(): void {
    try {
      const cacheData: CachedAmenities = {
        data: this.amenities,
        timestamp: Date.now()
      }
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      console.warn('[MEIUCA-APP] Erro ao salvar cache no localStorage:', error)
    }
  }

  private isCacheValidFromTimestamp(timestamp: number): boolean {
    return (Date.now() - timestamp) < this.CACHE_DURATION
  }

  private isCacheValid(): boolean {
    return this.amenitiesLoaded
  }

  private buildAmenitiesMap(): void {
    this.amenitiesMap.clear()
    this.amenities.forEach(amenity => {
      this.amenitiesMap.set(amenity.id, amenity)
    })
  }

  async loadAmenities(): Promise<Amenity[]> {
    // Se já existe uma requisição em andamento, aguarda ela
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Se o cache ainda é válido, retorna os dados em cache
    if (this.isCacheValid()) {
      return this.amenities
    }

    // Cria uma nova promessa para carregar as amenities
    this.loadingPromise = this.fetchAmenitiesFromAPI()

    try {
      const amenities = await this.loadingPromise
      this.amenities = amenities
      this.buildAmenitiesMap()
      this.amenitiesLoaded = true
      this.saveToLocalStorage()
      return amenities
    } catch (error) {
      console.error('[MEIUCA-APP] Erro ao carregar amenities:', error)
      throw error
    } finally {
      this.loadingPromise = null
    }
  }

  private async fetchAmenitiesFromAPI(): Promise<Amenity[]> {
    return amenityService.getAmenities()
  }

  async getAmenities(): Promise<Amenity[]> {
    return this.loadAmenities()
  }

  getAmenitiesSync(): Amenity[] {
    if (!this.amenitiesLoaded) {
      console.warn('[MEIUCA-APP] Amenities não foram carregadas ainda. Use loadAmenities() primeiro.')
      return []
    }
    return this.amenities
  }

  getAmenityById(id: string): Amenity | undefined {
    return this.amenitiesMap.get(id)
  }

  getAmenityLabel(id: string): string {
    const amenity = this.getAmenityById(id)
    return amenity?.label || id
  }

  getAmenityIcon(id: string): string {
    const amenity = this.getAmenityById(id)
    return amenity?.icon || ''
  }

  getAmenitiesMap(): Map<string, Amenity> {
    return this.amenitiesMap
  }

  getAmenitiesLabels(ids: string[]): string[] {
    return ids.map(id => this.getAmenityLabel(id))
  }

  isLoaded(): boolean {
    return this.amenitiesLoaded
  }

  clearCache(): void {
    this.amenities = []
    this.amenitiesMap.clear()
    this.amenitiesLoaded = false
    localStorage.removeItem(this.CACHE_KEY)
  }

  refreshAmenities(): Promise<Amenity[]> {
    this.clearCache()
    return this.loadAmenities()
  }
}

// Exporta a instância singleton
export const amenitiesManager = AmenitiesManager.getInstance()
export default amenitiesManager
